import React, { useCallback, useEffect, useState } from "react";
import { fetchServiceNumbers, updateServiceNumber } from "../services/api";
import { getUserRole, getUserPermissions } from "../services/authService";

const LABELS = {
  promotional: "Promotional",
  campaign: "Campaign",
  support: "Support",
  brilliant_plus: "Brilliant Plus",
};

const NUMBER_PATTERN = /^\+?\d{3,20}$/;

const ServiceNumbers = () => {
  const isAdmin = getUserRole() === "admin";
  const canEdit = isAdmin || getUserPermissions()?.service_numbers?.edit;

  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingName, setSavingName] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchServiceNumbers();
      setItems(res.data);
      setDrafts(Object.fromEntries(res.data.map((item) => [item.name, item.number])));
    } catch (err) {
      setError(err.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (item) => {
    const number = (drafts[item.name] || "").trim();
    setError("");
    setMessage("");
    if (!NUMBER_PATTERN.test(number)) {
      setError("Enter digits only (3–20), with an optional leading +.");
      return;
    }
    setSavingName(item.name);
    try {
      const res = await updateServiceNumber(item.name, number);
      setItems((current) => current.map((row) => (row.name === item.name ? res.data : row)));
      setDrafts((current) => ({ ...current, [item.name]: res.data.number }));
      setMessage(`${LABELS[item.name] || item.name} number updated.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingName(null);
    }
  };

  return (
    <div className="page-container">
      <h2 className="page-title">Service Numbers</h2>

      {error && <div style={{ color: "#dc2626", marginBottom: 12 }}>{error}</div>}
      {message && <div style={{ color: "#059669", marginBottom: 12 }}>{message}</div>}

      <div className="table-container">
        <table className="call-logs-table">
          <thead>
            <tr className="table-header-row">
              <th className="table-header">SERVICE</th>
              <th className="table-header">NUMBER</th>
              {canEdit && <th className="table-header">ACTIONS</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={canEdit ? 3 : 2} className="loading-cell">Loading...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={canEdit ? 3 : 2} className="empty-cell">No service numbers found</td></tr>
            ) : (
              items.map((item) => {
                const dirty = (drafts[item.name] || "").trim() !== item.number;
                return (
                  <tr key={item.name} className="table-row">
                    <td className="table-cell">{LABELS[item.name] || item.name}</td>
                    <td className="table-cell">
                      {canEdit ? (
                        <input
                          className="filter-input"
                          type="text"
                          value={drafts[item.name] ?? ""}
                          onChange={(e) => setDrafts({ ...drafts, [item.name]: e.target.value })}
                        />
                      ) : (
                        item.number
                      )}
                    </td>
                    {canEdit && (
                      <td className="table-cell">
                        <button
                          type="button"
                          className="rate-btn"
                          disabled={!dirty || savingName === item.name}
                          onClick={() => handleSave(item)}
                        >
                          {savingName === item.name ? "Saving..." : "Save"}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ServiceNumbers;
